import {
    EntityType,
    SnapshotImageContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { cloneDeep } from 'lodash-es';
import { ShareDto, ShareItemDto } from 'src/app/dtos/share.dto';
import { Content } from './content';

export class ShareItem {
    private raw: Partial<ShareItemDto>;

    constructor(shareItemDto?: Partial<ShareItemDto>) {
        this.raw = { ...shareItemDto };
    }

    get entityType(): EntityType {
        return this.raw.entityType || 'Unknown';
    }
    set entityType(entityType: EntityType) {
        this.raw.entityType = entityType;
    }

    get id(): string {
        return this.raw.id || '';
    }
    set id(id: string) {
        this.raw.id = id;
    }

    toDto(): ShareItemDto {
        return cloneDeep(this.raw) as ShareItemDto;
    }
}

export class Share {
    private _content: Content;
    private raw: Partial<ShareDto>;

    constructor(dto?: Partial<ShareDto>) {
        this.raw = cloneDeep(dto || {}) as ShareDto;
    }

    get content(): Content {
        return this._content;
    }
    set content(content: Content) {
        this._content = content;
    }

    get id(): string {
        return this.raw.id || '';
    }
    set id(id: string) {
        this.raw.id = id;
    }

    get title(): string {
        return this.raw.title || '';
    }
    set title(title: string) {
        this.raw.title = title;
    }

    get publicDisplayTitle(): string {
        return this.raw.publicDisplayTitle || '';
    }
    set publicDisplayTitle(title: string) {
        this.raw.publicDisplayTitle = title;
    }

    get riderId(): string {
        return this.raw.riderId || '';
    }
    set riderId(riderId: string) {
        this.raw.riderId = riderId;
    }

    get sharedByName(): string {
        return this.raw.sharedByName || '';
    }
    set sharedByName(sharedByName: string) {
        this.raw.sharedByName = sharedByName;
    }

    get appSku(): string {
        return this.raw.appSku || '';
    }
    set appSku(appSku: string) {
        this.raw.appSku = appSku;
    }

    get deepLink(): string {
        return this.raw.deepLink || '';
    }
    set deepLink(deepLink: string) {
        this.raw.deepLink = deepLink;
    }

    get sharedItems(): readonly ShareItem[] {
        return this.raw.sharedItems
            ? this.raw.sharedItems.map((s) => new ShareItem(s))
            : [];
    }
    set sharedItems(sharedItems: readonly ShareItem[]) {
        this.raw.sharedItems = [...sharedItems.map((s) => s.toDto())];
    }

    get isActive(): boolean {
        return Boolean(this.raw.isActive);
    }
    set isActive(isActive: boolean) {
        this.raw.isActive = isActive;
    }

    get snapshots(): readonly SnapshotImageContract[] {
        return this.raw.snapshots || [];
    }

    get creationDate(): Date | undefined {
        return this.raw.creationDate
            ? new Date(this.raw.creationDate)
            : undefined;
    }

    get campaign(): string {
        return this.raw.campaign || '';
    }
    set campaign(campaign: string) {
        this.raw.campaign = campaign;
    }

    get channel(): string {
        return this.raw.channel || '';
    }
    set channel(channel: string) {
        this.raw.channel = channel;
    }

    get feature(): string {
        return this.raw.feature || '';
    }
    set feature(feature: string) {
        this.raw.feature = feature;
    }

    get tags(): readonly string[] {
        return this.raw.tags || [];
    }
    set tags(tags: readonly string[]) {
        this.raw.tags = [...tags];
    }

    clone(): Share {
        const share = new Share(this.toDto());
        share.content = this.content?.clone();
        return share;
    }

    toDto(): ShareDto {
        return cloneDeep(this.raw) as ShareDto;
    }

    // create a clone with specified shared item.
    cloneWithShareItem(entityType: EntityType, id: string): Share {
        const cloned = this.clone();
        const newSharedItem = new ShareItem();
        newSharedItem.entityType = entityType;
        newSharedItem.id = id;
        cloned.sharedItems = [newSharedItem];
        return cloned;
    }
}
